import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiBriefcase, FiSearch, FiPlus, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import ProjectsList from './ProjectsList';
import { normalizeRole } from '../../finance/auth/permissions';
import ConfirmModal from '../shared/ConfirmModal';
import tasksApi from '../../api/tasksApi';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

export default function ProjectDashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const role = localStorage.getItem('role') || '';
  const normalizedRole = normalizeRole(role);
  const isAdmin = normalizedRole === 'ADMIN' || normalizedRole === 'SUPERADMIN' || normalizedRole === 'TESTER';

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [users, setUsers] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [formData, setFormData] = useState({
    projectCode: '',
    projectName: '',
    description: '',
    status: 'ACTIVE',
    clientName: '',
    projectManagerId: '',
    budget: '',
    startDate: '',
    endDate: '',
    productId: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, loading: false });

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await api.get('/project/getAllProjects');
      setProjects(res.data || []);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    tasksApi.getAssignees().then(list => {
      if (Array.isArray(list)) setUsers(list);
    }).catch(() => {});
    tasksApi.getProducts().then(list => {
      if (Array.isArray(list)) setProductsList(list);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    try {
      if (!formData.projectName?.trim()) {
        toast.error('Project name is required');
        return;
      }
      if (!formData.projectCode?.trim()) {
        toast.error('Project code is required');
        return;
      }
      
      const payload = {
        ...formData,
        projectName: formData.projectName.trim(),
        projectCode: formData.projectCode.trim(),
        description: formData.description?.trim() || null,
        clientName: formData.clientName?.trim() || null,
        budget: formData.budget !== '' && !isNaN(Number(formData.budget)) ? Number(formData.budget) : null,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        projectManagerId: formData.projectManagerId || null,
        productId: formData.productId ? Number(formData.productId) : null
      };
      if (isEditing) {
        await api.put(`/project/${editingId}`, payload);
        toast.success('Project updated successfully');
      } else {
        await api.post('/project/create', payload);
        toast.success('Project created successfully');
      }
      
      setShowModal(false);
      fetchProjects();
    } catch (err) {
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} project`);
    }
  };

  const openEditModal = (project) => {
    setEditingId(project.id || project.projectId);
    setIsEditing(true);
    setFormData({
      projectCode: project.projectCode || '',
      projectName: project.projectName || '',
      description: project.description || '',
      status: project.status || 'ACTIVE',
      clientName: project.clientName || '',
      projectManagerId: project.projectManagerId || '',
      budget: project.budget !== null && project.budget !== undefined ? project.budget : '',
      startDate: project.startDate ? String(project.startDate).split('T')[0] : '',
      endDate: project.endDate ? String(project.endDate).split('T')[0] : '',
      productId: project.productId || project.product?.id || ''
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ open: true, id, loading: false });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.id) return;
    setDeleteConfirm(prev => ({ ...prev, loading: true }));
    try {
      await api.delete(`/project/delete/${deleteConfirm.id}`);
      toast.success('Project deleted');
      setDeleteConfirm({ open: false, id: null, loading: false });
      fetchProjects();
    } catch (err) {
      toast.error('Failed to delete project');
      setDeleteConfirm(prev => ({ ...prev, loading: false }));
    }
  };

  const filteredProjects = projects.filter(p => 
    p.projectName?.toLowerCase().includes(search.toLowerCase()) ||
    p.projectCode?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
    <div className="space-y-6 animate-fade-in pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FiBriefcase className="text-blue-600" /> Projects
          </h2>
          <p className="text-slate-500 text-sm mt-1">Directory of all ongoing and past projects.</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search projects..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm"
            />
          </div>
          {isAdmin && (
            <button 
              onClick={() => {
                setIsEditing(false);
                setEditingId(null);
                setFormData({
                  projectCode: '',
                  projectName: '',
                  description: '',
                  status: 'ACTIVE',
                  clientName: '',
                  projectManagerId: '',
                  budget: '',
                  startDate: '',
                  endDate: '',
                  productId: ''
                });
                setShowModal(true);
              }}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl font-medium transition-colors shadow-sm cursor-pointer shrink-0"
            >
              <FiPlus /> New Project
            </button>
          )}
        </div>
      </div>

      <ProjectsList projects={filteredProjects} loading={loading} isAdmin={isAdmin} onEdit={openEditModal} onDelete={handleDelete} />
    </div>

      {showModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex justify-end p-4 sm:p-6">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl flex flex-col h-full overflow-hidden transform transition-transform duration-300 translate-x-0">
            <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
              <h3 className="text-lg font-bold text-slate-800">{isEditing ? 'Edit Project' : 'Create New Project'}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 bg-white shadow-sm border border-slate-200 rounded-full p-1.5"><FiX size={16} /></button>
            </div>
            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Project Code *</label>
                <input 
                  type="text" 
                  value={formData.projectCode}
                  onChange={(e) => setFormData({...formData, projectCode: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  placeholder="e.g. PRJ-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Project Name *</label>
                <input 
                  type="text" 
                  value={formData.projectName}
                  onChange={(e) => setFormData({...formData, projectName: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  placeholder="e.g. Website Redesign"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
                <textarea 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  placeholder="What is this project about?"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Client</label>
                  <input 
                    type="text" 
                    value={formData.clientName}
                    onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                    placeholder="e.g. Pfizer, Internal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Project Manager / Lead</label>
                  <select 
                    value={formData.projectManagerId}
                    onChange={(e) => setFormData({...formData, projectManagerId: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm bg-white cursor-pointer"
                  >
                    <option value="">-- No Manager Assigned --</option>
                    {users.map(u => (
                      <option key={u.id || u.username} value={u.username}>
                        {u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username} (@{u.username}){u.department ? ` • ${u.department}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
                  <select 
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm bg-white"
                  >
                    <option value="PLANNING">Planning</option>
                    <option value="ACTIVE">Active</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="REVIEW">Review</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="ON_HOLD">On Hold</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Associated Product</label>
                  <select 
                    value={formData.productId}
                    onChange={(e) => setFormData({...formData, productId: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm bg-white cursor-pointer"
                  >
                    <option value="">-- Standalone (No Product) --</option>
                    {productsList.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.productname || p.name || `Product #${p.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Budget ($ / ₹)</label>
                  <input 
                    type="number" 
                    value={formData.budget}
                    onChange={(e) => setFormData({...formData, budget: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                    placeholder="e.g. 50000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Start Date</label>
                  <input 
                    type="date" 
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">End Date</label>
                  <input 
                    type="date" 
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 shrink-0">
              <button 
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-200 bg-slate-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors"
              >
                {isEditing ? 'Save Changes' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, loading: false })}
        onConfirm={confirmDelete}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        confirmText="Delete"
        loading={deleteConfirm.loading}
      />
    </>
  );
}
